/* ──────────────────────────────────────────────────────────────
 *  Explore Designs — column definitions per bushing type
 *
 *  Each bushing type has a specific set of columns (metrics)
 *  displayed in the DataGrid, matching the legacy Python logic.
 * ────────────────────────────────────────────────────────────── */

import type { GridColDef } from '@mui/x-data-grid';
import type { BushingType } from '@/types/bushing';

export interface ExploreColumnDef {
  field: string;
  headerName: string;
  width?: number;
  type?: 'number' | 'string';
}

const COLUMNS_A1: ExploreColumnDef[] = [
  { field: 'id', headerName: 'Sample ID', width: 100, type: 'string' },
  { field: 'Cx', headerName: 'Axial [N/mm]', width: 120, type: 'number' },
  { field: 'Cz', headerName: 'Radial [N/mm]', width: 120, type: 'number' },
  { field: 'Torsion', headerName: 'Torsion [Nm/°]', width: 130, type: 'number' },
  { field: 'Conical', headerName: 'Conical [Nm/°]', width: 130, type: 'number' },
  { field: 'RUBBER_ANGLE_FACTOR', headerName: 'Rubber Angle Factor', width: 160, type: 'number' },
  { field: 'SWG_PCT', headerName: 'Swaging [%]', width: 110, type: 'number' },
  { field: 'CORE_OD', headerName: 'Core OD [mm]', width: 120, type: 'number' },
];

const COLUMNS_B1: ExploreColumnDef[] = [
  { field: 'id', headerName: 'Sample ID', width: 100, type: 'string' },
  { field: 'Cx', headerName: 'Axial [N/mm]', width: 120, type: 'number' },
  { field: 'Cz', headerName: 'Radial [N/mm]', width: 120, type: 'number' },
  { field: 'Torsion', headerName: 'Torsion [Nm/°]', width: 130, type: 'number' },
  { field: 'Conical', headerName: 'Conical [Nm/°]', width: 130, type: 'number' },
  { field: 'RUBBER_ANGLE_FACTOR_INNER', headerName: 'Angle Factor Inner', width: 150, type: 'number' },
  { field: 'RUBBER_ANGLE_FACTOR_OUTER', headerName: 'Angle Factor Outer', width: 150, type: 'number' },
  { field: 'SWG_PCT', headerName: 'Swaging [%]', width: 110, type: 'number' },
  { field: 'CORE_OUTER_DIAMETER', headerName: 'Core OD [mm]', width: 120, type: 'number' },
];

const COLUMNS_C1: ExploreColumnDef[] = [
  { field: 'id', headerName: 'Sample ID', width: 100, type: 'string' },
  { field: 'Cx', headerName: 'Axial [N/mm]', width: 120, type: 'number' },
  { field: 'Cz', headerName: 'Radial [N/mm]', width: 120, type: 'number' },
  { field: 'Torsion', headerName: 'Torsion [Nm/°]', width: 130, type: 'number' },
  { field: 'Conical', headerName: 'Conical [Nm/°]', width: 130, type: 'number' },
  { field: 'RUBBER_ANGLE_FACTOR', headerName: 'Rubber Angle Factor', width: 160, type: 'number' },
  { field: 'SWG_PCT', headerName: 'Swaging [%]', width: 110, type: 'number' },
  { field: 'CORE_OD', headerName: 'Core OD [mm]', width: 120, type: 'number' },
];

const COLUMNS_D1: ExploreColumnDef[] = [
  { field: 'id', headerName: 'Sample ID', width: 100, type: 'string' },
  { field: 'Cx', headerName: 'Axial [N/mm]', width: 120, type: 'number' },
  { field: 'Cy', headerName: 'Radial Void [N/mm]', width: 140, type: 'number' },
  { field: 'Cz', headerName: 'Radial Solid [N/mm]', width: 145, type: 'number' },
  { field: 'Torsion', headerName: 'Torsion [Nm/°]', width: 130, type: 'number' },
  { field: 'Conical', headerName: 'Conical [Nm/°]', width: 130, type: 'number' },
  { field: 'angle_factor', headerName: 'Angle Factor', width: 120, type: 'number' },
  { field: 'SHAPEFACTOR_INNERCORE', headerName: 'Shape Factor', width: 120, type: 'number' },
  { field: 'SWG_PCT', headerName: 'Swaging [%]', width: 110, type: 'number' },
  { field: 'DIAM_CENTER_INNER_CORE', headerName: 'Core OD [mm]', width: 120, type: 'number' },
];

const COLUMNS_BYD_VOIDED: ExploreColumnDef[] = [
  { field: 'id', headerName: 'Sample ID', width: 100, type: 'string' },
  { field: 'Cx', headerName: 'Axial [N/mm]', width: 120, type: 'number' },
  { field: 'Cy', headerName: 'Radial Void [N/mm]', width: 140, type: 'number' },
  { field: 'Cz', headerName: 'Radial Solid [N/mm]', width: 145, type: 'number' },
  { field: 'angle_factor', headerName: 'Angle Factor', width: 120, type: 'number' },
];

const COLUMNS_BYD_SOLID: ExploreColumnDef[] = [
  { field: 'id', headerName: 'Sample ID', width: 100, type: 'string' },
  { field: 'Cx', headerName: 'Axial [N/mm]', width: 120, type: 'number' },
  { field: 'Cz', headerName: 'Radial Solid [N/mm]', width: 145, type: 'number' },
  { field: 'SWG_PCT', headerName: 'Swaging [%]', width: 110, type: 'number' },
];

/** Get column definitions for the given bushing type. */
export function getExploreColumns(bushingType: BushingType): ExploreColumnDef[] {
  switch (bushingType) {
    case 'A1': return COLUMNS_A1;
    case 'B1': return COLUMNS_B1;
    case 'C1': return COLUMNS_C1;
    case 'D1': return COLUMNS_D1;
    case 'BYD EV Voided': return COLUMNS_BYD_VOIDED;
    case 'BYD EV Solid': return COLUMNS_BYD_SOLID;
  }
}

/** Get filterable metric options (numeric columns excluding 'id'). */
export function getExploreMetrics(bushingType: BushingType): string[] {
  return getExploreColumns(bushingType)
    .filter((col) => col.type === 'number')
    .map((col) => col.headerName);
}

/** Get the field key for a given header name. */
export function getFieldForMetric(bushingType: BushingType, headerName: string): string | null {
  const col = getExploreColumns(bushingType).find((c) => c.headerName === headerName);
  return col?.field ?? null;
}

/** Get the display header name for the primary radial (Cz) metric. */
export function getRadialMetricHeader(bushingType: BushingType): string {
  const col = getExploreColumns(bushingType).find((c) => c.field === 'Cz');
  return col?.headerName ?? 'Radial [N/mm]';
}

/** Convert ExploreColumnDef[] to MUI DataGrid GridColDef[]. */
export function toGridColDefs(columns: ExploreColumnDef[]): GridColDef[] {
  return columns.map((col) => ({
    field: col.field,
    headerName: col.headerName,
    flex: 1,
    minWidth: 108,
    type: col.type === 'number' ? 'number' : 'string',
    sortable: true,
    valueFormatter: col.type === 'number'
      ? (value: unknown) => {
          if (value == null) return '';
          const num = Number(value);
          return isNaN(num) ? '' : num.toFixed(1);
        }
      : undefined,
  }));
}
